<template>
  <f7-page name="shop-product-page">
    <f7-navbar :title="$t('shopProduct.title')" back-link />
    <AppLayout>
      <template #main>
        <f7-block v-if="loading"><f7-preloader /></f7-block>
        <f7-block v-else-if="error">{{ error }}</f7-block>
        <template v-else>
          <f7-swiper pagination v-if="selectedImages.length">
            <f7-swiper-slide
              v-for="image in selectedImages"
              :key="image.id || image.url"
            >
              <img class="product-image" :src="image.url" :alt="product.name" />
            </f7-swiper-slide>
          </f7-swiper>

          <f7-block strong inset>
            <h1>{{ product.name }}</h1>
            <p>{{ product.description }}</p>

            <f7-list v-if="product.variants?.length" inset>
              <f7-list-item :title="$t('common.variant') || 'Variant'">
                <template #input>
                  <f7-input v-model="selectedVariantId" type="select">
                    <option
                      v-for="variant in product.variants"
                      :key="variant.id"
                      :value="variant.id"
                    >
                      {{ variant.name }} -
                      {{
                        formatMoney(
                          variant.currentUnitPrice || product.currentPrice,
                        )
                      }}
                    </option>
                  </f7-input>
                </template>
              </f7-list-item>
            </f7-list>

            <div class="price">{{ formatMoney(currentPrice) }}</div>
            <p v-if="product.memberPrice" class="member-price">
              {{
                $t("shopProduct.memberPrice", {
                  price: formatMoney(product.memberPrice),
                })
              }}
            </p>

            <div class="stepper-row">
              <span class="stepper-label">{{
                $t("common.quantity") || "Quantity"
              }}</span>
              <f7-stepper
                v-model:value="quantity"
                :min="1"
                :max="stock"
                raised
              />
            </div>

            <div class="actions-grid">
              <f7-button fill large round raised @click="buyNow">{{
                $t("shopProduct.buyNow")
              }}</f7-button>
              <f7-button tonnal large round raised @click="addToCart">{{
                $t("shopProduct.addToCart")
              }}</f7-button>
              <f7-button outline large round raised @click="startGroupBuy">{{
                $t("shopProduct.startGroupBuy")
              }}</f7-button>
              <f7-button
                v-if="product.negotiable"
                outline
                large
                round raised
                @click="offerOpened = true"
              >
                {{ $t("shopProduct.proposePrice") }}
              </f7-button>
            </div>
          </f7-block>

          <f7-block v-if="groupBuy" strong inset class="group-buy-block">
            <h3>{{ groupBuy.title }}</h3>
            <p>
              {{
                $t("sharedCart.participants", {
                  count: groupBuy.participantCount,
                })
              }}
            </p>
            <f7-progressbar :progress="groupBuy.progress" />
            <p class="remaining-text">{{ groupBuy.remainingText }}</p>
            <div v-for="tier in groupBuy.tiers" :key="tier.id" class="tier">
              <span
                >{{ tier.minimumParticipants }}
                {{ $t("common.buyers") || "buyers" }}</span
              >
              <strong>{{ formatMoney(tier.unitPrice) }}</strong>
            </div>
          </f7-block>
        </template>
      </template>
    </AppLayout>

    <AdaptiveSheet
      v-model:opened="offerOpened"
      swipe-to-close
      close-on-escape
      :tablet-fullscreen="false"
      close-by-outside-click
    >
      <f7-page-content>
        <f7-block-title>{{ $t("shopProduct.proposePrice") }}</f7-block-title>
        <f7-list form inset strong media-list outline dividers>
          <f7-list-input
            v-model.number="offerPrice"
            type="number"
            :label="$t('shopProduct.yourPrice')"
            :placeholder="'0'"
          />
          <f7-list-input
            v-model="offerMessage"
            type="textarea"
            :label="$t('shopProduct.message')"
          />
        </f7-list>
        <f7-block>
          <f7-button fill large round raised @click="submitOffer">{{
            $t("shopProduct.reserveAndSubmit")
          }}</f7-button>
        </f7-block>
      </f7-page-content>
    </AdaptiveSheet>
  </f7-page>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { f7 } from "framework7-vue";
import { shopApi } from "@/services/shopApi";
import { useAuthStore } from "@/js/pinia-stores/auth.js";
import { usePaymentTrigger } from "@/js/composables/usePaymentTrigger.js";
import AppLayout from "@/components/common/AppLayout.vue";
import AdaptiveSheet from "../components/common/AdaptiveSheet.vue";
import i18n from "@/js/i18n.js";

const props = defineProps({
  productId: { type: String, required: true },
  f7route: Object,
  f7router: Object,
});
const authStore = useAuthStore();
const { t } = i18n.global;
const trigger = usePaymentTrigger({ f7router: props.f7router });

const product = ref({ variants: [], media: [] });
const groupBuy = ref(null);
const selectedVariantId = ref(null);
const quantity = ref(1);
const offerOpened = ref(false);
const offerPrice = ref(null);
const offerMessage = ref("");
const loading = ref(true);
const error = ref("");

onMounted(async () => {
  loading.value = true;
  error.value = "";
  try {
    const result = await shopApi.getProduct(props.productId);
    product.value = result.product || result;
    groupBuy.value = result.groupBuy || null;
    selectedVariantId.value = product.value.variants?.[0]?.id || null;
  } catch (err) {
    error.value = err.message || t("shopProduct.errorLoad");
  } finally {
    loading.value = false;
  }
});

const selectedVariant = computed(() =>
  product.value.variants?.find((v) => v.id === selectedVariantId.value),
);
const stock = computed(() => selectedVariant.value?.stockQuantity || 999);
const currentPrice = computed(
  () =>
    selectedVariant.value?.currentUnitPrice ||
    product.value.currentPrice ||
    100,
);
const selectedImages = computed(() =>
  selectedVariant.value?.images?.length
    ? selectedVariant.value.images
    : product.value.media || [],
);

function formatMoney(value) {
  if (value == null) return t("shop.contactSeller")||"Contact seller";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: product.value.currency || "EUR",
  }).format(value);
}

async function addToCart() {
  const userId = authStore.userId || "guest";
  const cart = await shopApi.createCart({ type: "PRIVATE", campaignId: null });
  await shopApi.addCartItem(cart.id, userId, {
    productId: props.productId,
    variantId: selectedVariantId.value,
    quantity: quantity.value,
  });
  const toast = f7.toast.create({
    text: t("shopProduct.addedToCart"),
    closeButton: true,
    closeButtonText: t("common.viewCart"),
    closeTimeout: 3000,
    on: {
      closeClick() {
        props.f7router?.navigate("/cart");
      },
    },
  });
  toast.open();
}

/**
 * Skip the cart entirely — hand a SHOP_LISTING-scoped PaymentTransactionRequest
 * to the unified payment page (Amazon/Shopify "Buy Now" pattern).
 */
function buyNow() {
  try {
    trigger.triggerListingPurchase({
      product: product.value,
      variant: selectedVariant.value,
      quantity: quantity.value,
      shopId: product.value.shopId || product.value.shop?.id,
    });
  } catch (err) {
    f7.dialog.alert(err?.message || t("shopProduct.errorLoad"));
  }
}

async function startGroupBuy() {
  const cart = await shopApi.createCart({
    type: "GROUP_BUY",
    campaignId: groupBuy.value?.id || null,
  });
  await shopApi.addCartItem(cart.id, authStore.userId || "guest", {
    productId: props.productId,
    variantId: selectedVariantId.value,
    quantity: quantity.value,
  });
  if (cart.id && props.f7router) {
    props.f7router.navigate(`/shared-cart/${cart.id}`);
  }
}

async function submitOffer() {
  await shopApi.submitOffer(props.productId, {
    variantId: selectedVariantId.value,
    offeredPrice: Number(offerPrice.value),
    currency: product.value.currency || "EUR",
    message: offerMessage.value,
  });
  offerOpened.value = false;
  f7.dialog.alert(
    t("shopProduct.offerSubmittedText"),
    t("shopProduct.offerSubmittedTitle"),
  );
}
</script>

<style scoped>
.product-image {
  width: 100%;
  height: 320px;
  object-fit: contain;
  background: #f4f6f8;
}
.price {
  font-size: 26px;
  font-weight: 750;
  margin-top: 10px;
  color: var(--f7-theme-color);
}
.member-price {
  color: var(--f7-on-surface-variant, #666);
  font-size: 13px;
  margin-top: 2px;
}
.stepper-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 16px 0;
}
.stepper-label {
  font-weight: 600;
  font-size: 14px;
}
.actions-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
}
.group-buy-block {
  background: color-mix(in srgb, var(--f7-theme-color) 4%, transparent);
}
.tier {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 13px;
  border-bottom: 1px solid var(--f7-list-item-border-color, #eee);
}
.remaining-text {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #666);
  margin: 6px 0 10px;
}
</style>
